





































































































































































































































import { VNode } from 'vue';
import { Component, Prop, Provide, Watch, Model } from 'vue-property-decorator';
import { Subject } from 'rxjs';
import _debounce from 'lodash/debounce';
import { SwExtendedVue } from '../sw-extended-vue';
import { SwIcon } from '../sw-icon';
import { LooseObject, CSSStyleOptions } from '../common-types';
import SwNavSideIndicator from './sw-nav-side-indicator.vue';
import { HighlightReferrerInfo, MyClientRect, ProvidedNavParam } from './types';
import { utilService } from '../util-service';
import { domService } from '../dom-service';

const cssPrefix = 'sw-nav';

/**
 * Stateless navigation component.
 *
 * Which nav item is selected should be controlled by the parent,
 * and passed in via `selected` property.
 *
 * Each SwNavGroup and SwNavItem should be using unique `nav-id`
 * across the nav structure.
 *
 * Events:
 * @event select `(itemId: string)`
 * @event request-change-collapsed `(isCollapsing: boolean)`
 */
@Component({
  components: {
    SwIcon,
    SwNavSideIndicator,
  },
})
export default class SwNav extends SwExtendedVue {
  cssPrefix = cssPrefix;

  @Prop({ type: [Number, String], default: '235px' })
  width: number | string;

  @Prop({ type: String, default: 'default' })
  size: ProvidedNavParam['size'];

  /** could be light, dark */
  @Prop({ type: String, default: 'dark' })
  theme: string;

  /** controlled selection value */
  @Prop({ type: String, required: true })
  selected: string;

  @Prop({ type: Boolean, default: false })
  accordion: boolean;

  @Prop({ type: Boolean, default: true })
  withLogo: boolean;

  @Prop({ type: Boolean, default: false })
  withPushMenu: boolean;

  @Prop({ type: Boolean, default: false })
  withCollapseTrigger: boolean;

  @Prop({ type: Boolean, default: false })
  putCollapseTriggerInside: boolean;

  @Prop({ type: String, default: 'black' })
  navColor: string;

  /** the `navData` will not be used to compose the nav components but just for
   * detecting the time when we want to update the selectedHierarchy. */
  @Prop({ default: null })
  navData: any;

  @Model('request-change-collapsed', { type: Boolean, default: false })
  collapsed: boolean;

  /** it has to be in the observable object `navParam` in order to make it reactive in children */
  @Provide()
  navParam: ProvidedNavParam = {
    eventbus: new Subject(),
    accordion: this.effectiveAccordion,
    theme: this.theme,
    collapsed: this.collapsed,
    selectedHierarchy: [],
    expandedGroups: [],
    navColor: this.navColor,
    size: this.size,
  };

  highlightReferrer: HighlightReferrerInfo | null = null;
  showDivider: boolean = true;
  private contentObserver: MutationObserver | null = null;

  get effectiveAccordion() {
    return this.accordion || this.collapsed;
  }

  get navWidth() {
    if (this.collapsed) { return '65px'; }
    if (typeof(this.width) === 'string') {
      return this.width;
    }
    return `${this.width}px`;
  }

  get contStyle(): CSSStyleOptions {
    return {
      width: this.navWidth,
    };
  }

  get rootStyle(): CSSStyleOptions {
    return this.contStyle;
  }

  @Watch('collapsed')
  private syncCollapsed() {
    this.navParam.collapsed = this.collapsed;
  }

  @Watch('effectiveAccordion')
  private syncEffectiveAccordion(val: boolean, oldVal: boolean) {
    this.navParam.accordion = this.effectiveAccordion;
    if (val && !oldVal) {
      /** only keep the last expanded group for each level */
      this.navParam.expandedGroups.forEach((levelReg) => {
        const length = levelReg.length;
        if (length <= 1) { return; }
        levelReg.splice(0, length - 1);
      });
    }
  }

  @Watch('theme')
  private syncTheme() {
    this.navParam.theme = this.theme;
  }

  @Watch('navColor')
  private syncNavColor() {
    this.navParam.navColor = this.navColor;
  }

  @Watch('size')
  private syncSize() {
    this.navParam.size = this.size;
  }

  @Watch('navData', { deep: true })
  @Watch('selected')
  private buildSelectedHierarchy() {
    const self = this;
    const tempHierarchy: string[] = [];

    function recordSelected(nodeArray: VNode[] | undefined): boolean {
      const arr = nodeArray ? nodeArray : [];
      let foundSelected = false;

      arr.forEach((node) => {
        if (!node.componentOptions || !node.componentOptions.propsData) {
          return;
        }
        if (
          node.componentOptions.tag !== 'sw-nav-group' &&
          node.componentOptions.tag !== 'sw-nav-item'
        ) {
          return;
        }

        if (
          (node.componentOptions.propsData as LooseObject).navId === self.selected ||
          recordSelected(node.componentOptions.children)
        ) {
          foundSelected = true;
          tempHierarchy.unshift((node.componentOptions.propsData as LooseObject).navId);
        }
      });

      return foundSelected;
    }

    /** we have to push the recording to next tick as `$scopedSlots` can only
     * get correct set of VNodes after the rendering.
     */
    this.$nextTick(() => {
      this.navParam.selectedHierarchy = tempHierarchy;

      if (!this.selected) {
        return;
      }

      recordSelected(utilService.getVueScopedSlot(this.$scopedSlots));
    });

    // if (this.collapsed) {
    //   if (this.navParam.selectedHierarchy.length === 1) {
    //     this.claimExpandedGroup(0, this.navParam.selectedHierarchy[0], true);
    //   } else {
    //     for (let i = 0; i < this.navParam.selectedHierarchy.length - 1; i = i + 1) {
    //       this.claimExpandedGroup(i, this.navParam.selectedHierarchy[i], true);
    //     }
    //   }
    // }
  }

  @Provide()
  private claimExpandedGroup(offsetLevel: number, groupId: string, expanded: boolean): void {
    const levelReg = this.navParam.expandedGroups[offsetLevel];
    if (levelReg) {
      const index = levelReg.indexOf(groupId);
      if (index >= 0 && !expanded) {
        levelReg.splice(index, 1);
      } else if (index < 0 && expanded) {
        if (this.effectiveAccordion) {
          levelReg.splice(0);
        }
        levelReg.push(groupId);
      }
    } else if (expanded) {
      this.$set(this.navParam.expandedGroups, offsetLevel, []);
      this.navParam.expandedGroups[offsetLevel].push(groupId);
    }
  }

  @Provide()
  private claimSelectNavItem(itemId: string) {
    this.$emit('select', itemId);
  }

  @Provide()
  private claimHighlightReferrer(navId: string, boundingRect: MyClientRect) {
    // const oldIndex = this.highlightReferrer ?
    //   this.navParam.selectedHierarchy.indexOf(this.highlightReferrer.navId) : -1;
    const newIndex = this.navParam.selectedHierarchy.indexOf(navId);

    if (newIndex >= 0) {
      /** we are trying to claim more upper-leveled highlight referrer */
      this.highlightReferrer = {
        navId,
        boundingRect,
      };
    }
  }

  @Provide()
  private claimCheckHighlightReferrerRequest() {
    this.$nextTick(() => {
      this.navParam.eventbus.next('request-check-highlight-referrer');
    });
  }

  private get debouncedClaimCheckHighlightReferrerRequest() {
    return _debounce(this.claimCheckHighlightReferrerRequest, 150);
  }

  private onClickCollapseTrigger() {
    this.$emit('request-change-collapsed', !this.collapsed);
  }

  private mounted() {
    this.buildSelectedHierarchy();
    this.contentObserver = domService.mutationObserverManager.setup(
      this.$el as HTMLElement,
      { childList: true, subtree: true, attributes: true, characterData: true },
      this.debouncedClaimCheckHighlightReferrerRequest,
    );
    (this.$el as HTMLElement).addEventListener('transitionend', this.debouncedClaimCheckHighlightReferrerRequest);
  }

  private beforeDestroy() {
    (this.$el as HTMLElement).removeEventListener('transitionend', this.debouncedClaimCheckHighlightReferrerRequest);
    if (this.contentObserver) {
      this.contentObserver.disconnect();
    }
  }
}
